import React, { useMemo } from 'react';

import { ReactComponent as ArrowNextIcon } from 'design-system/assets/arrow-next.svg';
import { ReactComponent as ArrowPreviousIcon } from 'design-system/assets/arrow-previous.svg';
import { ReactComponent as NextIcon } from 'design-system/assets/next.svg';
import { ReactComponent as PreviousIcon } from 'design-system/assets/previous.svg';

import {
  Container,
  Ellipsis,
  Item,
  List,
  NavIcon,
  PageButton,
  PaginationSize,
} from './styles';

export type PaginationProps = {
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  siblingCount?: number;
  disabled?: boolean;
  size?: PaginationSize;
  color?: string;
  activeColor?: string;
  activeBackgroundColor?: string;
  showFirstLast?: boolean;
  className?: string;
};

type PageItem = number | 'ellipsis';

const buildPages = (
  page: number,
  totalPages: number,
  siblingCount: number
): PageItem[] => {
  if (totalPages <= 1) return totalPages === 1 ? [1] : [];

  const totalNumbers = siblingCount * 2 + 3;
  const totalBlocks = totalNumbers + 2;

  if (totalPages <= totalBlocks) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  const leftSibling = Math.max(page - siblingCount, 1);
  const rightSibling = Math.min(page + siblingCount, totalPages);
  const showLeftEllipsis = leftSibling > 2;
  const showRightEllipsis = rightSibling < totalPages - 1;

  const pages: PageItem[] = [1];

  if (showLeftEllipsis) {
    pages.push('ellipsis');
  } else {
    for (let current = 2; current < leftSibling; current += 1) {
      pages.push(current);
    }
  }

  for (let current = leftSibling; current <= rightSibling; current += 1) {
    if (current !== 1 && current !== totalPages) {
      pages.push(current);
    }
  }

  if (showRightEllipsis) {
    pages.push('ellipsis');
  } else {
    for (let current = rightSibling + 1; current < totalPages; current += 1) {
      pages.push(current);
    }
  }

  if (totalPages > 1) {
    pages.push(totalPages);
  }

  return pages;
};

const Pagination = ({
  page,
  totalPages,
  onPageChange,
  siblingCount = 2,
  disabled = false,
  size = 'md',
  color,
  activeColor,
  activeBackgroundColor,
  showFirstLast = true,
  className,
}: PaginationProps): JSX.Element => {
  const pages = useMemo(
    () => buildPages(page, totalPages, siblingCount),
    [page, totalPages, siblingCount]
  );

  const goTo = (nextPage: number): void => {
    if (disabled) return;
    if (nextPage < 1 || nextPage > totalPages || nextPage === page) return;
    onPageChange(nextPage);
  };

  if (totalPages <= 0) return <></>;

  return (
    <Container className={className} aria-label="Paginação">
      <List>
        {showFirstLast && (
          <Item>
            <PageButton
              type="button"
              aria-label="Primeira página"
              $size={size}
              $nav
              $color={color}
              disabled={disabled || page === 1}
              onClick={() => goTo(1)}
            >
              <NavIcon>
                <PreviousIcon />
              </NavIcon>
            </PageButton>
          </Item>
        )}

        <Item>
          <PageButton
            type="button"
            aria-label="Página anterior"
            $size={size}
            $nav
            $color={color}
            disabled={disabled || page === 1}
            onClick={() => goTo(page - 1)}
          >
            <NavIcon>
              <ArrowPreviousIcon />
            </NavIcon>
          </PageButton>
        </Item>

        {pages.map((item, index) => (
          <Item key={`${item}-${index}`}>
            {item === 'ellipsis' ? (
              <Ellipsis $size={size}>…</Ellipsis>
            ) : (
              <PageButton
                type="button"
                aria-label={`Página ${item}`}
                aria-current={page === item ? 'page' : undefined}
                $size={size}
                $color={color}
                $active={page === item}
                $activeColor={activeColor}
                $activeBackgroundColor={activeBackgroundColor}
                disabled={disabled}
                onClick={() => goTo(item)}
              >
                {item}
              </PageButton>
            )}
          </Item>
        ))}

        <Item>
          <PageButton
            type="button"
            aria-label="Próxima página"
            $size={size}
            $nav
            $color={color}
            disabled={disabled || page === totalPages}
            onClick={() => goTo(page + 1)}
          >
            <NavIcon>
              <ArrowNextIcon />
            </NavIcon>
          </PageButton>
        </Item>

        {showFirstLast && (
          <Item>
            <PageButton
              type="button"
              aria-label="Última página"
              $size={size}
              $nav
              $color={color}
              disabled={disabled || page === totalPages}
              onClick={() => goTo(totalPages)}
            >
              <NavIcon>
                <NextIcon />
              </NavIcon>
            </PageButton>
          </Item>
        )}
      </List>
    </Container>
  );
};

export default Pagination;
